import { WallType } from "building-types/walls/wallEnums";
import type { WallLayerType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import { classifyContainmentVec2PolyLine2Fast } from "geom/containment";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import {
  CurveCurvePointIntersection,
  CurveCurveRangeIntersection,
  intersectLine2Line2,
} from "geom/intersectLine2Line2";
import { poleOfInaccessibilityPolyLine2 } from "geom/label";
import { offsetPolyLine2VariableStep } from "geom/offset";
import type { PlanarGraph } from "geom/planarGraph";
import {
  planarGraphAdjacentHalfEdge,
  planarGraphHalfEdgeLength,
  planarGraphHalfEdgeNormal,
  planarGraphVertexStripToPolyLine2,
  planarGraphNextCollinearHalfEdgeInLoop,
  planarGraphPrevCollinearHalfEdgeInLoop,
} from "geom/planarGraph";
import {
  polyLine2Area,
  polyLine2Close,
  polyLine2Concat,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeTangent,
  polyLine2IsClosed,
  polyLine2MergeCoincidentVertices,
  polyLine2MergeCollinearEdges,
  polyLine2MoveVertices,
  polyLine2ReplaceVertices,
  polyLine2SetCCW,
  polyLine2Shift,
  polyLine2Vertex,
  polyLine2VertexCount,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2DistSquared, vec2NormsCollinear } from "geom/vec2";
import { wallLayerWithFallback } from "../elements/walls/wallLayers";

interface HalfEdgeWallOffsets {
  readonly rightWallOffset: number;
  readonly leftWallOffset: number;
  readonly isRight: boolean | undefined;
}

export type HalfEdgeWallOffsetsCache = { [halfEdge: number]: HalfEdgeWallOffsets };

interface GetPolyLine2OutlineOptions {
  readonly wallLayer: WallLayerType;
  readonly planarGraph: PlanarGraph;
  readonly wallMassSystem: WallMassSystem;
  readonly wallOffsetCache: HalfEdgeWallOffsetsCache;
  readonly face: number;
  readonly faceHalfEdges: number[][];
  readonly faceVertices: number[][];
  readonly ignoreVoidLayers?: boolean;
  readonly useMaxWallStripOffset?: boolean;
}

export function polyLine2RoomOutlineByWallLayer({
  wallLayer,
  planarGraph,
  wallMassSystem,
  wallOffsetCache,
  faceHalfEdges,
  face,
  faceVertices,
  ignoreVoidLayers = false,
  useMaxWallStripOffset = false,
}: GetPolyLine2OutlineOptions): PolyLine2 {
  const halfEdges = faceHalfEdges[face];
  const { halfEdges: planarGraphHalfEdges, vertexHalfEdges } = planarGraph;
  const offsets: number[] = new Array<number>(halfEdges.length).fill(0);
  const vertexAdjustments: (number | undefined)[] = new Array<number | undefined>(halfEdges.length).fill(undefined);
  const removeIndices: { [index: number]: boolean } = {};
  const removeOffsetIndices: { [index: number]: boolean } = {};

  const wallOffsetCacheProps = {
    halfEdges,
    wallLayer,
    wallMassSystem,
    wallOffsetCache,
    planarGraph,
    ignoreVoidLayers,
    useMaxWallStripOffset,
  };

  for (let halfEdgeIndex = 0, halfEdgeCount = halfEdges.length; halfEdgeIndex < halfEdgeCount; halfEdgeIndex++) {
    const halfEdge = halfEdges[halfEdgeIndex];

    const startVertex = planarGraphHalfEdges[halfEdge * 2];
    const curHalfEdges = vertexHalfEdges[startVertex];
    const curHalfEdgesLength = curHalfEdges.length;

    //if intersection isn't three edges, then mark the offset as normal.
    if (curHalfEdgesLength !== 3) {
      getOrAddToCacheHalfEdgeWallOffsets({ ...wallOffsetCacheProps, halfEdge });
      continue;
    }

    const oppositeHalfEdges = curHalfEdges.map((x) => planarGraphAdjacentHalfEdge(x));

    const indexOfHalfEdge = curHalfEdges.indexOf(halfEdge);
    const indexOfPreviousRoomEdge = indexOfHalfEdge - 1;
    const wrappedPreviousIndex =
      indexOfPreviousRoomEdge < 0 ? curHalfEdgesLength + indexOfPreviousRoomEdge : indexOfPreviousRoomEdge;
    const previousRoomHalfEdge = oppositeHalfEdges[wrappedPreviousIndex];

    const [nx, ny] = planarGraphHalfEdgeNormal(planarGraph, halfEdge);
    const [nxPrev, nyPrev] = planarGraphHalfEdgeNormal(planarGraph, previousRoomHalfEdge);
    const previousRoomEdgeCollinear = vec2NormsCollinear(nx, ny, nxPrev, nyPrev);

    //if intersection isn't collinear, then mark the offset as normal.
    if (!previousRoomEdgeCollinear) {
      getOrAddToCacheHalfEdgeWallOffsets({ ...wallOffsetCacheProps, halfEdge });
      continue;
    }

    const wrappedPreviousHalfEdgeIndex = halfEdgeIndex < 1 ? halfEdgeCount - 1 : halfEdgeIndex - 1;
    const previousEdgeLength = planarGraphHalfEdgeLength(planarGraph, previousRoomHalfEdge);
    const wrappedSecondPreviousHalfEdgeIndex =
      wrappedPreviousHalfEdgeIndex < 1 ? halfEdgeCount - 1 : wrappedPreviousHalfEdgeIndex - 1;

    const {
      leftWallOffset: secondPrevLeft,
      rightWallOffset: secondPrevRight,
      isRight: secondPrevIsRight,
    } = getOrAddToCacheHalfEdgeWallOffsets({
      ...wallOffsetCacheProps,
      halfEdge: halfEdges[wrappedSecondPreviousHalfEdgeIndex],
    });

    if (previousEdgeLength <= (secondPrevIsRight ? secondPrevRight : secondPrevLeft)) {
      removeOffsetIndices[wrappedPreviousHalfEdgeIndex] = true;
      removeIndices[halfEdgeIndex] = true;
    }

    const edgeLength = planarGraphHalfEdgeLength(planarGraph, halfEdge);
    const wrappedNexHalfEdgeIndex = (halfEdgeIndex + 1) % halfEdgeCount;
    const {
      leftWallOffset: nextLeft,
      rightWallOffset: nextRight,
      isRight: nextIsRight,
    } = getOrAddToCacheHalfEdgeWallOffsets({
      ...wallOffsetCacheProps,
      halfEdge: halfEdges[wrappedNexHalfEdgeIndex],
    });

    if (edgeLength <= (nextIsRight ? nextRight : nextLeft)) {
      removeOffsetIndices[halfEdgeIndex] = true;
      removeIndices[halfEdgeIndex] = true;
    }

    const previousRoomEdgeOffsets = getOrAddToCacheHalfEdgeWallOffsets({
      ...wallOffsetCacheProps,
      halfEdge: previousRoomHalfEdge,
    });

    const previousRoomEdgeOffset = getRoomEdgeOffsetIntoRoom(previousRoomEdgeOffsets);
    const currentRoomEdgeOffsets = getOrAddToCacheHalfEdgeWallOffsets({ ...wallOffsetCacheProps, halfEdge });
    const currentRoomEdgeOffset = getRoomEdgeOffsetIntoRoom(currentRoomEdgeOffsets);

    //if intersection edges have the same wall offset, then mark the offset as normal.
    if (Math.abs(previousRoomEdgeOffset - currentRoomEdgeOffset) < DIST_EPSILON) {
      getOrAddToCacheHalfEdgeWallOffsets({ ...wallOffsetCacheProps, halfEdge });
      continue;
    }

    const wrappedNextIndex = (indexOfHalfEdge + 1) % curHalfEdgesLength;
    const nextHalfEdgeInRoom = oppositeHalfEdges[wrappedNextIndex];
    const { leftWallOffset, rightWallOffset, isRight } = getOrAddToCacheHalfEdgeWallOffsets({
      ...wallOffsetCacheProps,
      halfEdge: nextHalfEdgeInRoom,
    });

    const firstOffset = isRight ? rightWallOffset : leftWallOffset;
    const secondOffset = isRight ? leftWallOffset : rightWallOffset;

    const vertexAdjustmentDistance = previousRoomEdgeOffset > currentRoomEdgeOffset ? secondOffset : -firstOffset;
    vertexAdjustments[halfEdgeIndex] = vertexAdjustmentDistance;
  }

  for (let halfEdgeIndex = 0, halfEdgeCount = halfEdges.length; halfEdgeIndex < halfEdgeCount; halfEdgeIndex++) {
    const halfEdge = halfEdges[halfEdgeIndex];
    const { leftWallOffset, rightWallOffset, isRight } = getOrAddToCacheHalfEdgeWallOffsets({
      ...wallOffsetCacheProps,
      halfEdge,
    });
    offsets[halfEdgeIndex] = isRight ? rightWallOffset : leftWallOffset;
  }

  const pl = planarGraphVertexStripToPolyLine2(planarGraph, faceVertices[face], false);

  const filteredPlVerts: number[] = [];

  let secondX: number | undefined = undefined;
  let secondY: number | undefined = undefined;
  const vertexCount = polyLine2VertexCount(pl);
  for (let i = 0; i < vertexCount; i++) {
    if (removeIndices[i]) {
      if (i === 0) {
        [secondX, secondY] = polyLine2Vertex(pl, 1);
      }
      continue;
    }
    if (i === vertexCount - 1 && secondX !== undefined && secondY !== undefined) {
      filteredPlVerts.push(secondX, secondY);
    } else {
      const [x, y] = polyLine2Vertex(pl, i);
      filteredPlVerts.push(x, y);
    }
  }

  const pole = poleOfInaccessibilityPolyLine2(pl);
  let filteredPl = polyLine2New(filteredPlVerts);
  const filteredOffsets = offsets.filter((_, i) => !removeOffsetIndices[i]);
  const filteredVertexAdjustments = vertexAdjustments.filter((_, i) => !removeIndices[i]);

  for (const index of Object.keys(filteredVertexAdjustments)) {
    const adjustmentDist = filteredVertexAdjustments[index];
    if (adjustmentDist === undefined) {
      continue;
    }
    const [tx, ty] = polyLine2EdgeTangent(filteredPl, +index);
    const [moveX, moveY] = [tx * adjustmentDist, ty * adjustmentDist];
    filteredPl = polyLine2MoveVertices(filteredPl, [+index], moveX, moveY);
  }

  filteredPl = offsetPolyLine2VariableStep(filteredPl, filteredOffsets).pl;
  if (!polyLine2IsClosed(filteredPl)) {
    filteredPl = polyLine2Concat(filteredPl, [filteredPl[0], filteredPl[1]]);
  }
  filteredPl = polyLine2MergeCoincidentVertices(filteredPl);
  filteredPl = polyLine2MergeCollinearEdges(filteredPl);
  if (pole) {
    const [cx, cy] = pole;
    const result = polyLine2TrimSelfIntersections(filteredPl, cx, cy);
    if (result) {
      filteredPl = result;
    }
  }
  return polyLine2SetCCW(polyLine2Copy(filteredPl));
}

function getRoomEdgeOffsetIntoRoom(halfEdgeWallOffsets: HalfEdgeWallOffsets) {
  const { isRight, rightWallOffset, leftWallOffset } = halfEdgeWallOffsets;
  return isRight ? rightWallOffset : leftWallOffset;
}

function halfEdgeWallOffsetsFromLayer(
  wallMassSystem: WallMassSystem,
  wallLayer: WallLayerType,
  halfEdge: number,
  ignoreVoidLayers: boolean,
): HalfEdgeWallOffsets {
  const wallMassId = wallMassSystem.halfEdgeToWall[halfEdge];
  const wallMass = wallMassSystem.walls[wallMassId];
  const wallLayerMass = wallLayerWithFallback({ wall: wallMass, wallLayer });
  if (ignoreVoidLayers && (!wallLayerMass || wallLayerMass.wallType !== WallType.SOLID_MASS)) {
    return { leftWallOffset: 0, rightWallOffset: 0, isRight: undefined };
  }
  const { leftOffset: leftWallOffset, rightOffset: rightWallOffset, path } = wallLayerMass;
  const { halfEdgeIndex: wallHalfEdge } = path;
  const isRight = halfEdge === wallHalfEdge;
  return { leftWallOffset, rightWallOffset, isRight };
}

function getOrAddToCacheHalfEdgeWallOffsets({
  halfEdges,
  wallLayer,
  wallMassSystem,
  halfEdge,
  wallOffsetCache,
  planarGraph,
  ignoreVoidLayers,
  useMaxWallStripOffset,
}: {
  halfEdges: number[];
  wallLayer: WallLayerType;
  wallMassSystem: WallMassSystem;
  halfEdge: number;
  wallOffsetCache: HalfEdgeWallOffsetsCache;
  planarGraph: PlanarGraph;
  ignoreVoidLayers: boolean;
  useMaxWallStripOffset: boolean;
}): HalfEdgeWallOffsets {
  const cached = wallOffsetCache[halfEdge];
  if (cached) {
    return cached;
  }

  let offsets = halfEdgeWallOffsetsFromLayer(wallMassSystem, wallLayer, halfEdge, ignoreVoidLayers);

  // find the largest offset of all walls on the same room edge
  if (useMaxWallStripOffset) {
    let maxOffset = getRoomEdgeOffsetIntoRoom(offsets);
    const polygonPlanarGraph = wallMassSystem.wallIndex.planarGraph;

    const resolvedHE = planarGraphAdjacentHalfEdge(halfEdge);
    const startHEIndex = polygonPlanarGraph.halfEdgeIndexToPolygonEdgeIndex(resolvedHE);

    function findWallLayerMaxOffsetOnRoomEdge(
      loopIterator: (planarGraph: PlanarGraph, halfEdge: number, halfEdgeLoop: number[]) => number,
    ): number {
      let nextHE = loopIterator(planarGraph, halfEdge, halfEdges);
      while (nextHE !== -1) {
        const resolvedHE = planarGraphAdjacentHalfEdge(nextHE);
        const edgeIndex = polygonPlanarGraph.halfEdgeIndexToPolygonEdgeIndex(resolvedHE);
        if (edgeIndex !== startHEIndex) {
          break;
        }
        const currOffsets = halfEdgeWallOffsetsFromLayer(wallMassSystem, wallLayer, nextHE, ignoreVoidLayers);
        const currOffset = getRoomEdgeOffsetIntoRoom(currOffsets);
        if (currOffset > maxOffset) {
          maxOffset = currOffset;
        }
        nextHE = loopIterator(planarGraph, nextHE, halfEdges);
      }

      return maxOffset;
    }

    const prevMaxOffset = findWallLayerMaxOffsetOnRoomEdge(planarGraphPrevCollinearHalfEdgeInLoop);
    const nextMaxOffset = findWallLayerMaxOffsetOnRoomEdge(planarGraphNextCollinearHalfEdgeInLoop);
    maxOffset = Math.max(prevMaxOffset, nextMaxOffset, maxOffset);

    // set the max offset
    offsets = offsets.isRight ? { ...offsets, rightWallOffset: maxOffset } : { ...offsets, leftWallOffset: maxOffset };
  }
  wallOffsetCache[halfEdge] = offsets;
  return offsets;
}

/**
 * Generates and compares a new {@link HalfEdgeWallOffsetsCache} and returns
 * the new cache and room outline if the new cache is unique. Otherwise returns the original cache and an undefined outline.
 */
export function polyLine2RoomOutlineByWallLayerIfUniqueCache({
  wallLayer,
  planarGraph,
  wallMassSystem,
  wallOffsetCache,
  faceHalfEdges,
  face,
  faceVertices,
  ignoreVoidLayers = false,
  useMaxWallStripOffset = false,
}: GetPolyLine2OutlineOptions): {
  resultingWallOffsetCache: HalfEdgeWallOffsetsCache;
  resultingRoomOutline: PolyLine2 | undefined;
} {
  const halfEdges = faceHalfEdges[face];
  const newCache: HalfEdgeWallOffsetsCache = {};
  let isNewCacheUnique = false;

  for (const halfEdgeIndex of halfEdges) {
    getOrAddToCacheHalfEdgeWallOffsets({
      halfEdges,
      wallLayer,
      wallMassSystem,
      halfEdge: halfEdgeIndex,
      wallOffsetCache: newCache,
      planarGraph,
      ignoreVoidLayers,
      useMaxWallStripOffset,
    });

    if (!newCache[halfEdgeIndex]) {
      continue;
    }

    if (!wallOffsetCache[halfEdgeIndex]) {
      isNewCacheUnique = true;
      continue;
    }

    // compare to existing cache
    if (newCache[halfEdgeIndex].isRight !== wallOffsetCache[halfEdgeIndex].isRight) {
      isNewCacheUnique = true;
      continue;
    }

    if (newCache[halfEdgeIndex].isRight) {
      if (newCache[halfEdgeIndex].rightWallOffset !== wallOffsetCache[halfEdgeIndex].rightWallOffset) {
        isNewCacheUnique = true;
        continue;
      }
    } else {
      if (newCache[halfEdgeIndex].leftWallOffset !== wallOffsetCache[halfEdgeIndex].leftWallOffset) {
        isNewCacheUnique = true;
        continue;
      }
    }
  }

  if (isNewCacheUnique) {
    return {
      resultingWallOffsetCache: newCache,
      resultingRoomOutline: polyLine2RoomOutlineByWallLayer({
        wallLayer,
        planarGraph,
        wallMassSystem,
        wallOffsetCache: newCache,
        faceHalfEdges,
        face,
        faceVertices,
        ignoreVoidLayers,
        useMaxWallStripOffset,
      }),
    };
  }

  return {
    resultingWallOffsetCache: wallOffsetCache,
    resultingRoomOutline: undefined,
  };
}

function polyLine2TrimSelfIntersections(
  sourcePl: PolyLine2,
  insideTestPtX: number,
  insideTestPtY: number,
): PolyLine2 | undefined {
  let poly = polyLine2Copy(sourcePl);
  let closed = polyLine2IsClosed(poly);
  if (!closed) {
    poly = polyLine2Close(poly);
    closed = true;
  }

  if (!poly.length) {
    return sourcePl;
  }

  let leastRemappedVerticesCount = Number.MAX_SAFE_INTEGER;
  let bestPolygon: PolyLine2 | undefined = undefined;
  let bestPolygonArea: number = 0;
  let bestShift: number | undefined;
  let hasAtLeastOneSolution = false;

  const edgeCount = polyLine2EdgeCount(poly);

  for (let e = 0; e < edgeCount; e++) {
    let remappedVerticesCount = 0;

    let pl = e === 0 ? polyLine2Copy(poly) : polyLine2Shift(polyLine2Copy(poly), e);

    for (let i = 0, l = pl.length - 2; i < l; i += 2) {
      const ax = pl[i];
      const ay = pl[i + 1];

      const bx = pl[i + 2];
      const by = pl[i + 3];

      if (vec2DistSquared(ax, ay, bx, by) < DIST_EPSILON_SQUARED) {
        continue;
      }

      const rangeIntersectionAlreadyRepaired: { [index: number]: boolean } = {};
      let prevZeroLength = false;
      for (let j = i + 2; j < l; j += 2) {
        const cx = pl[j];
        const cy = pl[j + 1];

        const dx = pl[j + 2];
        const dy = pl[j + 3];

        // on next edge only allow detection of range intersections
        let onlyAllowRangeIntersection = false;
        if (j === i + 2) {
          onlyAllowRangeIntersection = true;
        } else if (j === (i + 4) % l && prevZeroLength) {
          onlyAllowRangeIntersection = true;
        }

        prevZeroLength = vec2DistSquared(cx, cy, dx, dy) < DIST_EPSILON_SQUARED;

        // looping around closed curve
        if (closed && j === (i - 2 + l) % l) {
          onlyAllowRangeIntersection = true;
        }

        const int = intersectLine2Line2(ax, ay, bx, by, cx, cy, dx, dy);

        if (!int) {
          continue;
        }

        if (int instanceof CurveCurveRangeIntersection) {
          const indices: number[] = [];
          for (let k = i / 2; k < j / 2; k++) {
            const current = (k + 1) % (pl.length / 2);
            remappedVerticesCount++;
            if (!rangeIntersectionAlreadyRepaired[current]) {
              rangeIntersectionAlreadyRepaired[current] = true;
              indices.push(current);
            }
          }

          pl = polyLine2ReplaceVertices(pl, indices, int.startx, int.starty);
        }

        if (!onlyAllowRangeIntersection && int instanceof CurveCurvePointIntersection) {
          const indices: number[] = [];

          for (let k = i / 2; k < j / 2; k++) {
            const current = (k + 1) % (pl.length / 2);
            remappedVerticesCount++;
            indices.push(current);
          }

          pl = polyLine2ReplaceVertices(pl, indices, int.x, int.y);
        }
      }
    }

    const area = polyLine2Area(pl);
    if (area < DIST_EPSILON_SQUARED || !classifyContainmentVec2PolyLine2Fast(insideTestPtX, insideTestPtY, pl)) {
      continue;
    }

    if (remappedVerticesCount <= leastRemappedVerticesCount && area > bestPolygonArea) {
      bestPolygon = polyLine2Copy(pl);
      bestPolygonArea = area;
      leastRemappedVerticesCount = remappedVerticesCount;
      bestShift = e;
      hasAtLeastOneSolution = true;
    }

    if (remappedVerticesCount === 0) {
      break;
    }
  }

  if (!hasAtLeastOneSolution) {
    return sourcePl;
  }

  if (bestShift !== undefined && bestShift !== 0 && bestPolygon) {
    bestPolygon = polyLine2Shift(bestPolygon, -1 * bestShift);
  }

  return !!bestPolygon ? polyLine2Copy(bestPolygon) : undefined;
}
